import Typography from '@mui/material/Typography'
import React, { useContext, useMemo } from 'react'

import ProfileContext from '@/components/Context/ProfileContext'
import { ProductListItem } from '@/components/Recommend/RecommendCard'
import RecommendSlot from '@/components/Recommend/RecommendSlot'
import useEnrichProducts from '@/lib/hook/useEnrichProducts'
import { useInternalRecommend } from '@/lib/hook/useInternalRecommend'

const MAX_SEEDS = 5
const MAX_SUGGESTIONS = 3

type Props = {
  companyId: number | string
  productIds: (number | string)[]
  amountFranco: number
  totalPrice: number
}

/**
 * Sous un vendeur dont le franco n'est pas atteint : ses produits souvent pris avec ce panier.
 * Rien ne s'ajoute seul : le bouton de la carte reste le seul geste.
 */
const BasketFrancoSuggestions: React.FC<Props> = ({ companyId, productIds, amountFranco, totalPrice }) => {
  const { profile } = useContext(ProfileContext)
  const missing = amountFranco > 0 && totalPrice < amountFranco
  const objectIDs = useMemo(() => productIds.slice(0, MAX_SEEDS).map(String), [productIds])
  const excludeObjectIDs = useMemo(() => productIds.map(String), [productIds])
  const enabled = !!profile?.recommendBlocksV2 && missing && objectIDs.length > 0

  const { products, loading } = useInternalRecommend({
    model: 'bought-with',
    objectIDs,
    companyId,
    maxRecommendations: MAX_SUGGESTIONS,
    excludeObjectIDs,
    enabled,
  })
  const { products: enriched } = useEnrichProducts(products, enabled && !loading)

  if (!enabled || enriched.length === 0) return null

  return (
    <div className="mt-3 border border-gray-200 rounded p-4">
      <Typography variant="h6" sx={{ fontSize: 14 }}>
        {t({ id: 'web_reco_franco_title' })}
      </Typography>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
        {t({ id: 'web_reco_franco_subtitle' })}
      </Typography>
      {/* Tuiles compactes (image a gauche) : une carte colonne laissait l'essentiel de sa hauteur vide */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {enriched.map((product, index) => (
          <RecommendSlot
            key={product.product_id}
            placement="basket:franco"
            productId={product.product_id}
            rank={index + 1}
            className="h-full"
          >
            <div className="h-full border border-solid border-gray-200 rounded-sm overflow-hidden bg-white">
              <ProductListItem product={product} tile />
            </div>
          </RecommendSlot>
        ))}
      </div>
    </div>
  )
}

export default BasketFrancoSuggestions
